import { useMemo, useState } from 'react';
import { Trash2, Search, Copy } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Field';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAdminMedia, useUpdateMediaAlt, useDeleteMedia, publicAssetUrl } from '@/lib/admin-queries';
import { toast } from '@/components/ui/Toast';

export function MediaPage() {
  const { data: rows = [], isLoading } = useAdminMedia();
  const updateAlt = useUpdateMediaAlt();
  const remove = useDeleteMedia();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'image' | 'document'>('all');

  const filtered = useMemo(() => {
    let list = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) => (r.alt as string | null)?.toLowerCase().includes(q) || (r.filename as string | null)?.toLowerCase().includes(q),
      );
    }
    if (filter === 'image') list = list.filter((r) => (r.mime as string | null)?.startsWith('image/'));
    if (filter === 'document') list = list.filter((r) => !((r.mime as string | null)?.startsWith('image/')));
    return list;
  }, [rows, search, filter]);

  async function onCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Media</p>
        <h1 className="mt-2 display-serif text-display-l text-fg">Library</h1>
        <p className="mt-2 text-sm text-fg-muted">Upload, browse, and tag images and files. Upload via the MediaField in any entity editor.</p>
      </header>

      <Card className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
            <Input
              placeholder="Search by filename or alt text…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-1.5">
            {(['all', 'image', 'document'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                className={
                  filter === k
                    ? 'rounded-md border border-gold-500/40 bg-gold-500/10 px-3 py-1.5 text-xs font-medium text-gold-500'
                    : 'rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-fg-muted hover:border-gold-500/40 hover:text-fg'
                }
              >
                {k === 'all' ? 'All' : k === 'image' ? 'Images' : 'Documents'}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-sm text-fg-subtle">
          No media. Upload files via any entity editor (Project hero, Team portrait, Blog hero…).
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((r) => {
            const url = publicAssetUrl(r.bucket as string, r.path as string);
            const isImage = (r.mime as string | null)?.startsWith('image/');
            return (
              <Card key={r.id as string} className="overflow-hidden">
                <div className="aspect-video w-full overflow-hidden bg-bg-subtle">
                  {isImage ? (
                    <img
                      src={url}
                      alt={(r.alt as string | null) ?? (r.filename as string) ?? ''}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-fg-subtle">
                      {r.mime as string}
                    </div>
                  )}
                </div>
                <div className="space-y-2 p-4">
                  <input
                    type="text"
                    defaultValue={(r.alt as string | null) ?? ''}
                    placeholder="Alt text"
                    onBlur={(e) => {
                      const next = e.target.value;
                      if (next !== (r.alt as string | null)) {
                        updateAlt.mutate({ id: r.id as string, alt: next });
                      }
                    }}
                    className="w-full rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
                  />
                  <p className="truncate text-[10px] text-fg-subtle">{r.filename as string}</p>
                  <p className="text-[10px] text-fg-subtle">
                    {(((r.bytes as number | null) ?? 0) / 1024).toFixed(1)} KB · {r.bucket as string}
                  </p>
                  <div className="flex items-center justify-between gap-1 pt-1">
                    <button
                      type="button"
                      onClick={() => onCopy(url)}
                      className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs text-fg-muted hover:border-gold-500/40 hover:text-fg"
                    >
                      <Copy className="h-3 w-3" /> Copy URL
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!confirm(`Delete ${r.filename as string}? This also removes the file from storage.`)) return;
                        remove.mutate({ id: r.id as string, bucket: r.bucket as string, path: r.path as string });
                      }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}